import { useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Heading1, Heading2, Italic, List, ListOrdered, Quote, Redo2, Strikethrough, Undo2 } from "lucide-react";

interface Props {
  html: string;
  onChange: (html: string) => void;
  focus: boolean;
}

export default function NotebookEditor({ html, onChange, focus }: Props) {
  const editor = useEditor({
    immediatelyRender: false,
    extensions: [StarterKit, Placeholder.configure({ placeholder: "Begin where you are…" })],
    content: html,
    editorProps: { attributes: { class: "prose-editor", spellcheck: "true" } },
    onUpdate: ({ editor: current }) => onChange(current.getHTML()),
  });

  useEffect(() => {
    if (editor && editor.getHTML() !== html) editor.commands.setContent(html, { emitUpdate: false });
  }, [editor, html]);

  if (!editor) return <div className="editor-loading">Opening your page…</div>;
  const words = editor.getText().trim().split(/\s+/).filter(Boolean).length;
  const button = (label: string, active: boolean, action: () => void, icon: React.ReactNode) => (
    <button type="button" className={active ? "active" : ""} title={label} aria-label={label} onClick={action}>{icon}</button>
  );

  return <section className="notebook-editor">
    {!focus && <div className="editor-toolbar">
      <div>{button("Undo", false, () => { editor.chain().focus().undo().run(); }, <Undo2 size={16} />)}{button("Redo", false, () => { editor.chain().focus().redo().run(); }, <Redo2 size={16} />)}</div>
      <i />
      <div>{button("Bold", editor.isActive("bold"), () => { editor.chain().focus().toggleBold().run(); }, <Bold size={16} />)}{button("Italic", editor.isActive("italic"), () => { editor.chain().focus().toggleItalic().run(); }, <Italic size={16} />)}{button("Strike", editor.isActive("strike"), () => { editor.chain().focus().toggleStrike().run(); }, <Strikethrough size={16} />)}</div>
      <i />
      <div>{button("Heading one", editor.isActive("heading", { level: 1 }), () => { editor.chain().focus().toggleHeading({ level: 1 }).run(); }, <Heading1 size={17} />)}{button("Heading two", editor.isActive("heading", { level: 2 }), () => { editor.chain().focus().toggleHeading({ level: 2 }).run(); }, <Heading2 size={17} />)}{button("Bulleted list", editor.isActive("bulletList"), () => { editor.chain().focus().toggleBulletList().run(); }, <List size={17} />)}{button("Numbered list", editor.isActive("orderedList"), () => { editor.chain().focus().toggleOrderedList().run(); }, <ListOrdered size={17} />)}{button("Quote", editor.isActive("blockquote"), () => { editor.chain().focus().toggleBlockquote().run(); }, <Quote size={17} />)}</div>
      <span className="toolbar-whisper">Saved gently, never loudly.</span>
    </div>}
    <div className="paper"><EditorContent editor={editor} /></div>
    {!focus && <footer><span>{words} words</span><span>{Math.max(1, Math.ceil(words / 220))} min read</span></footer>}
  </section>;
}
